import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useGetFoundPinsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapData, selectSelectedMarkerId } from '@repo/store/maps/slices/mapData';
import { useCallback, useMemo, useState } from 'react';
import { buildCategoryGroups, buildCheckedItems, buildChecklistItems, buildSubcategoryImageMap, type CompletionFilter, computeChecklistTotals, filterCategoryGroups, filterChecklistItems, groupItemsByCategory } from './checklist-utils';
import { useChecklistToggle } from './use-checklist-toggle';
type ChecklistScreen = {
  type: 'categories';
} | {
  type: 'subcategory';
  categoryId: string;
  subcategoryId: string;
  subcategoryTitle: string;
};
export function useChecklistState() {
  const $ = _c(58);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "categories"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [screen, setScreen] = useState(t0);
  const [searchQuery, setSearchQuery] = useState("");
  const [completionFilter, setCompletionFilter] = useState("all");
  const analytics = useAnalytics();
  const hostType = useHostType();
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const isLoggedIn = useAppSelector(_temp);
  const t1 = !isLoggedIn || !externalMapId;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      skip: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: foundPinsData
  } = useGetFoundPinsQuery(externalMapId ?? "", t2);
  let t3;
  if ($[3] !== foundPinsData || $[4] !== isLoggedIn) {
    t3 = buildCheckedItems(isLoggedIn, foundPinsData);
    $[3] = foundPinsData;
    $[4] = isLoggedIn;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const checkedItems = t3;
  let t4;
  bb0: {
    if (!mapData) {
      let t5;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    if ($[7] !== checkedItems || $[8] !== mapData) {
      t5 = buildCategoryGroups(mapData, checkedItems);
      $[7] = checkedItems;
      $[8] = mapData;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = t5;
  }
  const categoryGroups = t4;
  let t5;
  if ($[10] !== categoryGroups || $[11] !== completionFilter || $[12] !== searchQuery) {
    t5 = filterCategoryGroups(categoryGroups, searchQuery, completionFilter);
    $[10] = categoryGroups;
    $[11] = completionFilter;
    $[12] = searchQuery;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const filteredCategoryGroups = t5;
  let t6;
  bb1: {
    if (!mapData) {
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = new Map();
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      t6 = t7;
      break bb1;
    }
    let t7;
    if ($[15] !== mapData) {
      t7 = buildSubcategoryImageMap(mapData);
      $[15] = mapData;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = t7;
  }
  const subcategoryImageMap = t6;
  let t7;
  if ($[17] !== categoryGroups) {
    t7 = computeChecklistTotals(categoryGroups);
    $[17] = categoryGroups;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    totalChecklistItems,
    foundChecklistItems
  } = t7;
  let t8;
  bb2: {
    if (screen.type !== "subcategory" || !mapData) {
      let t9;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      t8 = t9;
      break bb2;
    }
    let t9;
    if ($[20] !== mapData || $[21] !== screen.subcategoryId) {
      t9 = buildChecklistItems(mapData, {
        subcategoryId: screen.subcategoryId
      });
      $[20] = mapData;
      $[21] = screen.subcategoryId;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    t8 = t9;
  }
  const subcategoryItems = t8;
  let t9;
  if ($[23] !== checkedItems || $[24] !== completionFilter || $[25] !== searchQuery || $[26] !== subcategoryItems) {
    t9 = filterChecklistItems(subcategoryItems, searchQuery, completionFilter, checkedItems);
    $[23] = checkedItems;
    $[24] = completionFilter;
    $[25] = searchQuery;
    $[26] = subcategoryItems;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const filteredSubcategoryItems = t9;
  let t10;
  if ($[28] !== filteredSubcategoryItems) {
    t10 = groupItemsByCategory(filteredSubcategoryItems);
    $[28] = filteredSubcategoryItems;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const groupedFilteredItems = t10;
  let t11;
  if ($[30] !== checkedItems || $[31] !== subcategoryItems) {
    let t12;
    if ($[33] !== checkedItems) {
      t12 = item => checkedItems.has(item.id);
      $[33] = checkedItems;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = subcategoryItems.filter(t12);
    $[30] = checkedItems;
    $[31] = subcategoryItems;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const foundSubcategoryItems = t11.length;
  const handleToggleItem = useChecklistToggle(externalMapId, checkedItems);
  let t12;
  if ($[35] !== analytics || $[36] !== externalMapId || $[37] !== hostType || $[38] !== screen.type) {
    t12 = value => {
      setSearchQuery(value);
      if (value.trim() && externalMapId) {
        analytics.event("game_map_checklist_search", {
          mapId: externalMapId,
          searchQuery: value,
          platform: hostType,
          checklistStep: screen.type === "subcategory" ? "subcategory" : "categories"
        });
      }
    };
    $[35] = analytics;
    $[36] = externalMapId;
    $[37] = hostType;
    $[38] = screen.type;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const handleSearchChange = t12;
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (categoryId, subcategoryId, subcategoryTitle) => {
      setScreen({
        type: "subcategory",
        categoryId,
        subcategoryId,
        subcategoryTitle
      });
      setSearchQuery("");
      setCompletionFilter("all");
    };
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const navigateToSubcategory = t13;
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setScreen({
        type: "categories"
      });
      setSearchQuery("");
      setCompletionFilter("all");
    };
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const navigateToCategories = t14;
  let t15;
  if ($[42] !== checkedItems || $[43] !== completionFilter || $[44] !== filteredCategoryGroups || $[45] !== foundChecklistItems || $[46] !== foundSubcategoryItems || $[47] !== groupedFilteredItems || $[48] !== handleSearchChange || $[49] !== handleToggleItem || $[50] !== isLoggedIn || $[51] !== screen || $[52] !== searchQuery || $[53] !== selectedMarkerId || $[54] !== subcategoryImageMap || $[55] !== subcategoryItems || $[56] !== totalChecklistItems) {
    t15 = {
      screen,
      searchQuery,
      handleSearchChange,
      completionFilter,
      setCompletionFilter,
      filteredCategoryGroups,
      subcategoryImageMap,
      totalChecklistItems,
      foundChecklistItems,
      subcategoryItems,
      foundSubcategoryItems,
      groupedFilteredItems,
      checkedItems,
      selectedMarkerId,
      isLoggedIn,
      handleToggleItem,
      navigateToSubcategory,
      navigateToCategories
    };
    $[42] = checkedItems;
    $[43] = completionFilter;
    $[44] = filteredCategoryGroups;
    $[45] = foundChecklistItems;
    $[46] = foundSubcategoryItems;
    $[47] = groupedFilteredItems;
    $[48] = handleSearchChange;
    $[49] = handleToggleItem;
    $[50] = isLoggedIn;
    $[51] = screen;
    $[52] = searchQuery;
    $[53] = selectedMarkerId;
    $[54] = subcategoryImageMap;
    $[55] = subcategoryItems;
    $[56] = totalChecklistItems;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  return t15;
}
function _temp(state) {
  return state.auth.isAuthenticated;
}